// src/components/ui/Motion.js
// Shared motion primitives for the rebrand.
import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  useInView as useFramerInView,
  animate,
} from 'framer-motion';

/* ------------------------------------------------------------------ */
/* Reveal — scroll-triggered fade/slide/blur in                        */
/* ------------------------------------------------------------------ */
export const Reveal = ({
  children,
  delay = 0,
  y = 28,
  once = true,
  amount = 0.25,
  as = 'div',
  ...rest
}) => {
  const ref = useRef(null);
  const inView = useFramerInView(ref, { once, amount });
  const MotionTag = motion[as] || motion.div;

  return (
    <MotionTag
      ref={ref}
      initial={{ opacity: 0, y, filter: 'blur(8px)' }}
      animate={
        inView
          ? { opacity: 1, y: 0, filter: 'blur(0px)' }
          : { opacity: 0, y, filter: 'blur(8px)' }
      }
      transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
      {...rest}
    >
      {children}
    </MotionTag>
  );
};

/* ------------------------------------------------------------------ */
/* Stagger container + child                                           */
/* ------------------------------------------------------------------ */
export const staggerContainer = (stagger = 0.09, delayChildren = 0.05) => ({
  hidden: {},
  visible: {
    transition: { staggerChildren: stagger, delayChildren },
  },
});

export const staggerItem = {
  hidden: { opacity: 0, y: 24, filter: 'blur(6px)' },
  visible: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: { duration: 0.65, ease: [0.16, 1, 0.3, 1] },
  },
};

/* ------------------------------------------------------------------ */
/* MagneticButton — element leans toward the cursor                    */
/* ------------------------------------------------------------------ */
export const MagneticButton = ({ children, strength = 0.4, as = 'button', ...rest }) => {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 200, damping: 15, mass: 0.3 });
  const sy = useSpring(y, { stiffness: 200, damping: 15, mass: 0.3 });

  const handleMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const relX = e.clientX - (rect.left + rect.width / 2);
    const relY = e.clientY - (rect.top + rect.height / 2);
    x.set(relX * strength);
    y.set(relY * strength);
  };
  const reset = () => {
    x.set(0);
    y.set(0);
  };

  const MotionTag = motion[as] || motion.button;

  return (
    <MotionTag
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={reset}
      style={{ x: sx, y: sy }}
      data-cursor="hover"
      {...rest}
    >
      {children}
    </MotionTag>
  );
};

/* ------------------------------------------------------------------ */
/* AnimatedCounter — counts up when scrolled into view                 */
/* Supports prefix/suffix and decimal values.                         */
/* ------------------------------------------------------------------ */
export const AnimatedCounter = ({
  value,
  prefix = '',
  suffix = '',
  decimals = 0,
  duration = 2,
  className,
}) => {
  const ref = useRef(null);
  const inView = useFramerInView(ref, { once: true, amount: 0.4 });
  const [display, setDisplay] = useState(prefix + '0' + suffix);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration,
      ease: [0.16, 1, 0.3, 1],
      onUpdate(latest) {
        setDisplay(prefix + latest.toFixed(decimals) + suffix);
      },
    });
    return () => controls.stop();
  }, [inView, value, prefix, suffix, decimals, duration]);

  return (
    <span ref={ref} className={className}>
      {display}
    </span>
  );
};

/* ------------------------------------------------------------------ */
/* TiltCard — 3D tilt on pointer, glare highlight                      */
/* ------------------------------------------------------------------ */
const TiltShell = styled(motion.div)`
  position: relative;
  transform-style: preserve-3d;
  will-change: transform;
`;

const Glare = styled(motion.div)`
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  background: radial-gradient(
    circle at var(--gx, 50%) var(--gy, 50%),
    rgba(255, 255, 255, 0.14),
    transparent 45%
  );
  opacity: 0;
  transition: opacity 0.3s ease;
`;

export const TiltCard = ({ children, className, max = 10, glare = true, ...rest }) => {
  const ref = useRef(null);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 200, damping: 20 });
  const sry = useSpring(ry, { stiffness: 200, damping: 20 });
  const [glarePos, setGlarePos] = useState({ x: 50, y: 50, on: false });

  const handleMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    ry.set((px - 0.5) * max * 2);
    rx.set(-(py - 0.5) * max * 2);
    setGlarePos({ x: px * 100, y: py * 100, on: true });
  };
  const reset = () => {
    rx.set(0);
    ry.set(0);
    setGlarePos((g) => ({ ...g, on: false }));
  };

  return (
    <TiltShell
      ref={ref}
      className={className}
      onMouseMove={handleMove}
      onMouseLeave={reset}
      style={{ rotateX: srx, rotateY: sry }}
      {...rest}
    >
      {children}
      {glare && (
        <Glare
          style={{
            '--gx': `${glarePos.x}%`,
            '--gy': `${glarePos.y}%`,
            opacity: glarePos.on ? 1 : 0,
          }}
        />
      )}
    </TiltShell>
  );
};

/* ------------------------------------------------------------------ */
/* GradientMesh — animated blurred blobs behind a section              */
/* ------------------------------------------------------------------ */
const MeshWrap = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;
`;

const Blob = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(${({ blur }) => blur || 90}px);
  opacity: ${({ opacity }) => opacity ?? 0.35};
  animation: floatSlow ${({ dur }) => dur || 18}s ease-in-out infinite;
  will-change: transform;
`;

export const GradientMesh = ({ blobs }) => (
  <MeshWrap aria-hidden="true">
    {blobs.map((b, i) => (
      <Blob
        key={i}
        blur={b.blur}
        opacity={b.opacity}
        dur={b.dur}
        style={{
          width: b.size,
          height: b.size,
          top: b.top,
          left: b.left,
          right: b.right,
          bottom: b.bottom,
          background: b.color,
          animationDelay: `${(b.delay || 0)}s`,
        }}
      />
    ))}
  </MeshWrap>
);

/* Parallax helper: returns a y motion value driven by scroll progress */
export const useParallax = (value, distance) =>
  useTransform(value, [0, 1], [-distance, distance]);
